import React, { createContext, useCallback, useContext, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useIsAuthenticated, useIsInternalUser } from '@/authentication';
import { useLocalStorage } from '@/browser-storage';
import { logError } from '@/errors';
import { GetGatewayConfigs, GetGatewayConfigsContext } from '@/generated/graphql';
import { gql, useQuery } from '@/graphql';
import { DEFAULT_GATEWAY_CONFIGS, GatewayConfigs } from './gateway-config-model';
import { getGatewayConfigOverridesFromEnv, getURLParamsGatewayConfigOverrides } from './gateway-config-overrides';

const GET_GATEWAY_CONFIGS = gql`
  query GetGatewayConfigs {
    gatewayConfigs {
      returnedSeriesLimit
      returnedDatapointsLimit
      tracingUIEnabled
      collectionsAccess
      serviceUIEnabled
      sloUIEnabled
      enableExternalServiceConfig
      enableMetricsDDX
      alertEntityUIEnabled
      aiGatewayEnabled
      agentInvestigationsEnabled
    }
    tenant {
      graphiteEnabled
      tenantConfiguration {
        scrapeInterval
      }
    }
  }
`;

export const GatewayConfigsContext = createContext<{
  isLoading: boolean;
  gatewayConfigs: GatewayConfigs;
  setGatewayConfigs: (configs: GatewayConfigs) => void;
  originalGatewayConfigs: GatewayConfigs;
  reset: () => void;
}>({
  isLoading: false,
  originalGatewayConfigs: DEFAULT_GATEWAY_CONFIGS,
  gatewayConfigs: DEFAULT_GATEWAY_CONFIGS,
  setGatewayConfigs: () => {
    // noop
  },
  reset: () => {
    // noop
  },
});

export function GatewayConfigsContextProvider(props: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const [searchParams] = useSearchParams();

  const [{ data, error, fetching }] = useQuery<GetGatewayConfigs>({
    query: GET_GATEWAY_CONFIGS,
    context: GetGatewayConfigsContext,
    pause: !isAuthenticated,
  });

  // Since we "handle" this error instead of throwing it, make sure it gets reported
  useEffect(() => {
    if (error === undefined || error === null) return;
    logError(error);
  }, [error]);

  const configs =
    data && !error
      ? {
          ...data?.gatewayConfigs,
          ...data?.tenant?.tenantConfiguration,
          graphiteEnabled: data?.tenant?.graphiteEnabled ?? false,
        }
      : DEFAULT_GATEWAY_CONFIGS;

  const isChronoAdmin = useIsInternalUser();
  const envConfigLocalOverrides = getGatewayConfigOverridesFromEnv();

  const [localStorageGatewayConfigs, setLocalStorageGatewayConfigs] = useLocalStorage('gatewayConfigsOverrides', {});

  // This hook is responsible for checking if there are any URL params that override the gateway configs
  useEffect(() => {
    const paramOverrides = getURLParamsGatewayConfigOverrides(searchParams.toString());
    // If there are URL param overrides, we need to update the local storage overrides with them
    if (Object.keys(paramOverrides).length > 0) {
      // Confirm with the user if they have existing local storage overrides
      if (Object.keys(localStorageGatewayConfigs).length > 0) {
        const confirmed = window.confirm(
          'You have opened a link with URL gateway config overrides but you have existing overrides set. Do you want to apply the overrides from the URL? Any existing overrides will be overwritten.'
        );
        if (!confirmed) {
          return;
        }
      }
      setLocalStorageGatewayConfigs(paramOverrides);
    }
    // Only do this once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the local storage overrides to original state
  const reset = useCallback(() => {
    setLocalStorageGatewayConfigs({}); // Clear the local storage overrides
  }, [setLocalStorageGatewayConfigs]);

  // Only merge the configs if the user is a Chrono Admin
  const mergedConfigs = isChronoAdmin
    ? {
        ...configs,
        ...envConfigLocalOverrides,
        ...localStorageGatewayConfigs,
      }
    : configs;

  return (
    <GatewayConfigsContext.Provider
      value={{
        isLoading: fetching,
        originalGatewayConfigs: configs,
        gatewayConfigs: mergedConfigs,
        setGatewayConfigs: setLocalStorageGatewayConfigs,
        reset,
      }}
    >
      {props.children}
    </GatewayConfigsContext.Provider>
  );
}

export function useGatewayConfigsState() {
  const { gatewayConfigs, setGatewayConfigs, originalGatewayConfigs, reset } = useContext(GatewayConfigsContext);
  return {
    originalGatewayConfigs,
    gatewayConfigs,
    setGatewayConfigs,
    reset,
  };
}

export function useGatewayConfigs() {
  const { gatewayConfigs } = useGatewayConfigsState();
  return gatewayConfigs;
}

export function areGatewayConfigsLoading(gatewayConfigs: GatewayConfigs) {
  // Gateway configs will return the default values object until data has
  // been loaded from the server, so wait until we have server data
  return gatewayConfigs === DEFAULT_GATEWAY_CONFIGS;
}
